import { type FormEvent, useEffect, useState } from "react";

import "./sourceExplorer.css";
import {
  type ParsedDocumentView,
  type RegionDetail,
  type RegionLevel,
  type RegionSummary,
  type SourceInventory,
  type VersionDetail,
  describeLocator,
  getParsedDocument,
  getRegion,
  getRegions,
  getSources,
  getVersion,
} from "./explorerClient";
import { RegionPanel, Withheld } from "./RegionPanel";

const PAGE = 100;
const ROOT = "";

/** What the explorer shows; mirrored in the query string so every anchor has a link. */
export interface ExplorerLocation {
  engagement?: string;
  version?: string;
  parsed?: string;
  anchor?: string;
}

function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

function readLocation(): ExplorerLocation {
  const params = new URLSearchParams(typeof window === "undefined" ? "" : window.location.search);
  return {
    engagement: params.get("engagement") ?? undefined,
    version: params.get("version") ?? undefined,
    parsed: params.get("parsed") ?? undefined,
    anchor: params.get("anchor") ?? undefined,
  };
}

export function explorerHref(location: ExplorerLocation): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(location)) {
    if (value) params.set(key, value);
  }
  const text = params.toString();
  return `/source-explorer${text ? `?${text}` : ""}`;
}

function size(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MiB`;
}

function RegionNode({
  region,
  levels,
  expanded,
  selected,
  onToggle,
  onMore,
  onSelect,
}: {
  region: RegionSummary;
  levels: Record<string, RegionLevel>;
  expanded: Set<string>;
  selected?: string;
  onToggle: (key: string) => void;
  onMore: (key: string) => void;
  onSelect: (key: string) => void;
}) {
  const open = expanded.has(region.region_key);
  const children = levels[region.region_key];
  const where = describeLocator({ ...region.locator, bbox: undefined, coordinate_space: undefined });
  return (
    <li>
      {region.child_count > 0 ? (
        <button
          type="button"
          className="se-toggle"
          aria-expanded={open}
          aria-label={`${open ? "Collapse" : "Expand"} ${region.region_key}`}
          onClick={() => onToggle(region.region_key)}
        >
          {open ? "▾" : "▸"}
        </button>
      ) : (
        <span className="se-toggle" aria-hidden="true" />
      )}
      <button
        type="button"
        className="se-node"
        aria-current={selected === region.region_key ? "true" : undefined}
        onClick={() => onSelect(region.region_key)}
      >
        <code>{region.region_key}</code> <span className="se-kind">{region.region_kind}</span>
        {where ? <span className="se-where"> {where}</span> : null}
        {region.label ? <span className="se-label"> “{region.label}”</span> : null}
        {region.is_hidden ? <span className="se-badge"> hidden</span> : null}
        {region.finding_count > 0 ? (
          <span className="se-badge"> {region.finding_count} finding(s)</span>
        ) : null}
        {region.action !== "none" ? (
          <span className={`se-badge se-${region.action}`}> {region.action}</span>
        ) : null}
      </button>
      {open && children && (
        <ul>
          {children.items.map((child) => (
            <RegionNode
              key={child.region_key}
              region={child}
              levels={levels}
              expanded={expanded}
              selected={selected}
              onToggle={onToggle}
              onMore={onMore}
              onSelect={onSelect}
            />
          ))}
          {children.total > children.items.length && (
            <li className="se-more">
              {children.items.length} of {children.total} shown{" "}
              <button type="button" onClick={() => onMore(region.region_key)}>
                Show more
              </button>
            </li>
          )}
        </ul>
      )}
    </li>
  );
}

/** Source explorer (DESIGN-SPEC 14, 28.2): the original inventory, each original's parses, the
 * parsed hierarchy, and every anchor resolved to its exact place in the original. */
export function SourceExplorerPage() {
  const [location, setLocation] = useState<ExplorerLocation>(readLocation);
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [inventory, setInventory] = useState<SourceInventory | null>(null);
  const [version, setVersion] = useState<VersionDetail | null>(null);
  const [parsed, setParsed] = useState<ParsedDocumentView | null>(null);
  const [levels, setLevels] = useState<Record<string, RegionLevel>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [detail, setDetail] = useState<RegionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.history.replaceState(window.history.state, "", explorerHref(location));
    }
  }, [location]);

  useEffect(() => {
    if (!location.engagement) return;
    let active = true;
    getSources({ engagement_id: location.engagement, q: search, limit: PAGE, offset })
      .then((value) => active && setInventory(value))
      .catch((caught: unknown) => active && setError(message(caught)));
    return () => {
      active = false;
    };
  }, [location.engagement, search, offset]);

  useEffect(() => {
    if (!location.version) return;
    let active = true;
    getVersion(location.version)
      .then((value) => active && setVersion(value))
      .catch((caught: unknown) => active && setError(message(caught)));
    return () => {
      active = false;
    };
  }, [location.version]);

  useEffect(() => {
    if (!location.parsed) return;
    const parsedId = location.parsed;
    let active = true;
    Promise.all([getParsedDocument(parsedId), getRegions(parsedId)])
      .then(async ([document, root]) => {
        const keys = root.items.map((r) => r.region_key);
        const opened = await Promise.all(keys.map((key) => getRegions(parsedId, key)));
        if (!active) return;
        setParsed(document);
        setLevels((current) => {
          const next: Record<string, RegionLevel> = { ...current, [ROOT]: root };
          keys.forEach((key, index) => {
            const level = opened[index];
            if (level) next[key] = level;
          });
          return next;
        });
        setExpanded((current) => new Set([...current, ...keys]));
      })
      .catch((caught: unknown) => active && setError(message(caught)));
    return () => {
      active = false;
    };
  }, [location.parsed]);

  useEffect(() => {
    if (!location.parsed || !location.anchor) return;
    const parsedId = location.parsed;
    let active = true;
    getRegion(parsedId, location.anchor)
      .then(async (region) => {
        if (!active) return;
        setDetail(region);
        // Open the hierarchy along the anchor path so the region is visible in its context.
        const keys = region.ancestors.map((a) => a.region_key);
        const opened = await Promise.all(keys.map((key) => getRegions(parsedId, key)));
        if (!active) return;
        setLevels((current) => {
          const next: Record<string, RegionLevel> = { ...current };
          keys.forEach((key, index) => {
            const level = opened[index];
            if (level) next[key] = level;
          });
          return next;
        });
        setExpanded((current) => new Set([...current, ...keys]));
      })
      .catch((caught: unknown) => active && setError(message(caught)));
    return () => {
      active = false;
    };
  }, [location.parsed, location.anchor]);

  function go(next: ExplorerLocation) {
    setError(null);
    if (next.version !== location.version) setVersion(null);
    if (next.parsed !== location.parsed) {
      setParsed(null);
      setLevels({});
      setExpanded(new Set());
    }
    if (next.anchor !== location.anchor || next.parsed !== location.parsed) setDetail(null);
    setLocation(next);
  }

  function onScope(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const engagement = String(form.get("engagement") ?? "").trim() || undefined;
    setSearch(String(form.get("q") ?? "").trim());
    setOffset(0);
    if (engagement !== location.engagement) {
      setInventory(null);
      go({ engagement });
    }
  }

  /** The next page of a level's children, appended to those already shown. */
  async function more(key: string) {
    const shown = levels[key];
    if (!shown || !location.parsed) return;
    try {
      const next = await getRegions(location.parsed, key || undefined, shown.items.length);
      setLevels((current) => {
        const items = current[key]?.items ?? [];
        return { ...current, [key]: { ...next, offset: 0, items: [...items, ...next.items] } };
      });
    } catch (caught) {
      setError(message(caught));
    }
  }

  async function toggle(key: string) {
    if (expanded.has(key)) {
      setExpanded((current) => new Set([...current].filter((k) => k !== key)));
      return;
    }
    if (!levels[key] && location.parsed) {
      try {
        const level = await getRegions(location.parsed, key);
        setLevels((current) => ({ ...current, [key]: level }));
      } catch (caught) {
        setError(message(caught));
        return;
      }
    }
    setExpanded((current) => new Set([...current, key]));
  }

  const selectAnchor = (anchor: string) => go({ ...location, anchor });
  const root = levels[ROOT];

  return (
    <section aria-labelledby="explorer-title" className="se">
      <h1 id="explorer-title">Source explorer</h1>
      {error && <p role="alert">{error}</p>}

      <form onSubmit={onScope} aria-label="Explorer scope">
        <label>
          Engagement id <input name="engagement" defaultValue={location.engagement ?? ""} />
        </label>
        <label>
          File name contains <input name="q" defaultValue={search} />
        </label>
        <button type="submit">Show sources</button>
      </form>

      <div className="se-layout">
        <section className="se-pane" aria-label="Originals">
          <h2>Originals</h2>
          {inventory ? (
            <>
              <p data-testid="inventory-counts">
                {inventory.counts.versions} version(s) of {inventory.counts.sources} source(s):{" "}
                {inventory.counts.parsed} parsed, {inventory.counts.not_parsed} not parsed,{" "}
                {inventory.counts.quarantined} quarantined, {inventory.counts.incomplete}{" "}
                incomplete, {inventory.counts.restricted} restricted
              </p>
              <ul className="se-sources">
                {inventory.items.map((row) => (
                  <li key={row.version_id}>
                    <button
                      type="button"
                      className="se-node"
                      aria-current={location.version === row.version_id ? "true" : undefined}
                      onClick={() =>
                        go({
                          engagement: location.engagement,
                          version: row.version_id,
                          parsed: row.latest_parse?.parsed_document_id,
                        })
                      }
                    >
                      {row.original_filename} <span className="se-kind">v{row.version}</span>
                    </button>
                    <div className="se-meta">
                      {row.rights.access_classification}
                      {row.content_access.allowed ? "" : " · content withheld"} ·{" "}
                      {size(row.byte_size)} ·{" "}
                      {row.latest_parse
                        ? `${row.latest_parse.parser_name} ${row.latest_parse.parse_status}${row.latest_parse.quarantined ? " · quarantined" : ""}`
                        : "not parsed"}
                    </div>
                  </li>
                ))}
              </ul>
              <nav aria-label="Source pages">
                <button
                  type="button"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={offset + PAGE >= inventory.filtered}
                  onClick={() => setOffset(offset + PAGE)}
                >
                  Next
                </button>
              </nav>
            </>
          ) : (
            <p>Enter an engagement to list its originals.</p>
          )}
        </section>

        <section className="se-pane" aria-label="Parsed hierarchy">
          {version && (
            <div className="se-original se-pane" data-representation="original">
              <span className="se-tag se-tag-original">Original</span>{" "}
              <strong>{version.original.filename}</strong> v{version.version}
              <dl className="se-facts">
                <dt>SHA-256</dt>
                <dd>
                  <code data-testid="original-sha256">{version.original.sha256}</code>
                </dd>
                <dt>Size</dt>
                <dd>{size(version.original.byte_size)}</dd>
                <dt>Signature</dt>
                <dd>
                  {version.original.signature ?? "no longer held"} (declared{" "}
                  {version.original.declared_media_type ?? "none"})
                </dd>
                <dt>Rights</dt>
                <dd>
                  {version.rights.access_classification}, provider exposure{" "}
                  {version.rights.provider_exposure}
                  {version.rights.legal_hold ? ", legal hold" : ""}
                  {version.rights.rag_corpus_allowed ? "" : ", kept out of the RAG corpus"}
                </dd>
              </dl>
              {!version.content_access.allowed && <Withheld access={version.content_access} />}
              <label>
                Parsed version{" "}
                <select
                  aria-label="Parsed version"
                  value={location.parsed ?? ""}
                  onChange={(event) =>
                    go({
                      engagement: location.engagement,
                      version: location.version,
                      parsed: event.target.value || undefined,
                    })
                  }
                >
                  <option value="">Choose a parse</option>
                  {version.parses.map((p) => (
                    <option key={p.parsed_document_id} value={p.parsed_document_id}>
                      {p.parser_name} {p.parser_version}
                      {p.ocr_engine ? ` (${p.ocr_engine} ${p.ocr_version})` : ""} · {p.parse_status}
                      {p.quarantined ? " · quarantined" : ""}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {parsed && (
            <div className="se-parsed se-pane" data-representation="parsed">
              <span className="se-tag se-tag-parsed">Parsed</span> {parsed.parser.name}{" "}
              {parsed.parser.version} · {parsed.parse_status} · {parsed.region_count} regions
              <p className="se-meta">
                Output SHA-256 <code>{parsed.output_sha256.slice(0, 16)}…</code> · parser
                fingerprint <code>{parsed.parser.fingerprint.slice(0, 12)}…</code>
                {parsed.quality.quarantined ? " · quarantined" : ""}
                {parsed.quality.incomplete ? " · incomplete" : ""}
              </p>
              {parsed.sibling_outputs && (
                <p className="se-meta" data-testid="sibling-outputs">
                  Sibling outputs: {parsed.sibling_outputs.knowledge_segment_units} knowledge
                  segment unit(s), {parsed.sibling_outputs.transcription_units} transcription
                  unit(s) of {parsed.sibling_outputs.text_units} text unit(s)
                </p>
              )}
              {root && (
                <ul className="se-tree" aria-label="Regions">
                  {root.items.map((region) => (
                    <RegionNode
                      key={region.region_key}
                      region={region}
                      levels={levels}
                      expanded={expanded}
                      selected={location.anchor}
                      onToggle={(key) => void toggle(key)}
                      onMore={(key) => void more(key)}
                      onSelect={selectAnchor}
                    />
                  ))}
                </ul>
              )}
              {parsed.findings.length > 0 && (
                <details open>
                  <summary>
                    {parsed.findings.length} finding(s): {parsed.quality.error_findings} error,{" "}
                    {parsed.quality.warning_findings} warning, {parsed.quality.info_findings} info
                  </summary>
                  <ul aria-label="Document findings">
                    {parsed.findings.map((f) => (
                      <li key={f.finding_id} className={`se-${f.severity}`}>
                        <strong>{f.finding_code}</strong> ({f.severity}
                        {f.action !== "none" ? `, ${f.action}` : ""}){" "}
                        {f.region_key ? (
                          <button
                            type="button"
                            className="se-link"
                            onClick={() => selectAnchor(f.region_key ?? "")}
                          >
                            {f.region_key}
                          </button>
                        ) : (
                          "whole document"
                        )}
                        {f.message !== null ? ` — ${f.message}` : ""}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </section>

        <section className="se-pane" aria-label="Region">
          {detail ? (
            <RegionPanel detail={detail} onNavigate={selectAnchor} />
          ) : (
            <p>Choose a region to see it in the original, as parsed, and as interpreted.</p>
          )}
        </section>
      </div>
    </section>
  );
}
